<template>
  <div
    :id="uniqId"
    :class="[
      $style.wrapper,
      $style[`${preset}Preset`],
      { [$style[position]]: isCustomPreset }
    ]"
    :style="wrapperStyles"
  >
    <transition :name="$style[animationName]">
      <div
        v-if="isShownModal"
        :class="$style.container"
        @click="handleContainerClick"
      >
        <!-- HEADER -->
        <div
          v-if="closable || slots.title"
          :class="$style.header"
        >
          <div
            v-if="slots.title"
            :class="$style.title"
          >
            <slot name="title" />
          </div>
          <div
            v-if="closable"
            :class="$style.icon"
            @click="handleCloseModal"
          >
            <BaseSvg
              path="icons/cross"
              :size="32"
              color="var(--color-icon-default)"
              actionable
            />
          </div>
        </div>

        <!-- CONTENT -->
        <div :class="$style.content">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, useId } from 'vue'

import {
  BaseModalPresetEnum,
  BaseModalPositionEnum,
  type BaseModalPresetType,
  type BaseModalPositionType,
  type BaseModalCustomPositionType
} from '@models/base/baseModal'

import { prepareCssValue } from '@scripts/helpers/cssHelpers'

import BaseSvg from '@components/base/BaseSvg.vue'

type BaseModalPropsType = {
  preset?: BaseModalPresetType;
  position?: BaseModalPositionType;
  customPosition?: BaseModalCustomPositionType;
  minWidth?: string | number;
  width?: string | number;
  maxWidth?: string | number;
  desktopPadding?: string | number;
  mobilePadding?: string | number;
  modalColor?: string;
  transformOrigin?: string;
  closable?: boolean;
  clickToClose?: boolean;
  zIndex?: number;
}

const {
  preset = BaseModalPresetEnum.primary,
  position = BaseModalPositionEnum.center,
  customPosition,
  minWidth = 300,
  width = 'auto',
  maxWidth = 500,
  desktopPadding = '8px',
  mobilePadding = '8px',
  modalColor = 'var(--color-white)',
  transformOrigin = 'center',
  closable = false,
  clickToClose = false,
  zIndex = 100
} = defineProps<BaseModalPropsType>()

const slots = defineSlots<{
  default?: () => any;
  title?: () => any;
}>()

const modelValue = defineModel<boolean>({ required: true })

const uniqId = useId()

const notifyingModalList = ref<Element[]>([])

const isShownModal = computed(() => modelValue.value)
const isSecondaryPreset = computed(() => preset === BaseModalPresetEnum.secondary)
const isNotifyingPreset = computed(() => preset === BaseModalPresetEnum.notifying)
const isCustomPreset = computed(() => preset === BaseModalPresetEnum.custom)
const isCustomPosition = computed(() => position === BaseModalPositionEnum.custom)

const animationName = computed(() => {
  if (isCustomPreset.value) {
    if ([
      BaseModalPositionEnum.left,
      BaseModalPositionEnum.top,
      BaseModalPositionEnum.right,
      BaseModalPositionEnum.bottom
    ].some(pos => pos === position)) {
      return `${position}Slide`
    }

    return 'scaleY'
  }

  return `${preset}PresetModalAnimation`
})

const wrapperStyles = computed(() => {
  return (isCustomPosition.value && customPosition) || {}
})

const containerStyles = computed(() => {
  return {
    width: isSecondaryPreset.value || isNotifyingPreset.value
      ? '100%'
      : isCustomPreset.value ? prepareCssValue(width) : 'auto',
    minWidth: prepareCssValue(minWidth),
    maxWidth: prepareCssValue(maxWidth),
    desktopPadding: prepareCssValue(desktopPadding),
    mobilePadding: prepareCssValue(mobilePadding),
    backgroundColor: modalColor,
    transformOrigin
  }
})

const handleCloseModal = () => {
  modelValue.value = false
}

const handleContainerClick = () => {
  if (clickToClose) {
    handleCloseModal()
  }
}

watch(isShownModal, (newVal: boolean) => {
  if (!isNotifyingPreset.value) {
    return
  }

  if (newVal) {
    let visibleModals = 0
    notifyingModalList.value = [...document.querySelectorAll('[class*="notifyingPreset"]')]
    notifyingModalList.value.forEach(notifyingModal => {
      const notifyingModalContainer = notifyingModal.firstChild

      if (notifyingModalContainer instanceof HTMLElement) {
        visibleModals++
        notifyingModalContainer.style.marginTop = prepareCssValue(14 + (visibleModals - 1) * 68)
      }
    })
  }
}, { flush: 'post' })
</script>

<style lang="scss" module>
@use "@styles/transitions/transitions.scss";

.wrapper {
  display: flex;
  z-index: v-bind(zIndex);
  pointer-events: none;
}

.container {
  width: v-bind('containerStyles.width');
  min-width: v-bind('containerStyles.minWidth');
  max-width: v-bind('containerStyles.maxWidth');
  padding: v-bind('containerStyles.desktopPadding');
  border-radius: 8px;
  background-color: var(--color-white);
  pointer-events: auto;
  overflow: hidden;
  transform-origin: v-bind('containerStyles.transformOrigin');
  @include flex-column();
  @include mobile-styles((padding: v-bind('containerStyles.mobilePadding')));
}

.wrapper:not(.notifyingPreset) {
  & > .container {
    backdrop-filter: blur(8px);
  }
}

.header {
  margin-bottom: 8px;
  padding: 12px;
  @include flex-row-start(20);
}

.title {
  width: 100%;
  word-break: break-word;
  @include font-type-bold(16, 28);
  @include mobile-styles((
    font-size: 24px,
    line-height: 24px
  ));
}

.icon {
  margin-left: auto;
  flex-shrink: 0;
  cursor: pointer;
  @include flex-center-row();
  @include size(32);
  @include mobile-styles((
    width: 24px,
    height: 24px
  ));
}

.content {
  height: 100%;
  padding: 12px;
  overflow: auto;
  word-break: break-word;
}

.center,
.left,
.top,
.right,
.bottom,
.primaryPreset,
.secondaryPreset {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.center {
  justify-content: center;
  align-items: center;
}

.left {
  justify-content: flex-start;
  align-items: center;
  border-left: none;

  & > .container {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.top {
  justify-content: center;
  align-items: flex-start;

  & > .container {
    border-top: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
}

.right {
  justify-content: flex-end;
  align-items: center;

  & > .container {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.bottom {
  justify-content: center;
  align-items: flex-end;

  & > .container {
    border-bottom: none;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.custom {
  position: absolute;
}

.primaryPreset {
  justify-content: center;
  align-items: center;

  @include adaptability-styles(500, ( align-items: flex-end ));

  & > .container {
    @include adaptability-styles(500, (
      width: 100%,
      border-bottom: none,
      border-bottom-right-radius: 0,
      border-bottom-left-radius: 0
    ));
  }
}

.secondaryPreset {
  justify-content: center;
  align-items: center;

  & > .container {
    height: 80vh;

    @include adaptability-styles(520, (
      height: 100%,
      border-top-right-radius: 0,
      border-top-left-radius: 0,
      border-bottom-right-radius: 0,
      border-bottom-left-radius: 0
    ));
  }
}

.notifyingPreset {
  position: fixed;
  top: 0;
  left: 10px;
  right: 10px;
  bottom: auto;
  justify-content: center;

  & > .container {
    position: relative;
    background-color: v-bind('containerStyles.backgroundColor');
    box-shadow: 0 1px 10px 0 rgba($color-grey, 0.25);
    transition: margin-top 0.15s ease-out, transform 0.15s ease-out, opacity 0.15s linear;
  }
}

.customPreset {
  & > .container {
    max-height: 80vh;
  }
}
</style>
